"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { buildPageWindow } from "@/lib/search/page-window";
import { cn } from "@/lib/utils";

interface SearchPaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  previousLabel: string;
  nextLabel: string;
}

/**
 * 검색 결과 목록의 페이지 나눔 막대.
 *
 * 규칙(`buildPageWindow`)은 admin `components/ui/list-pagination.tsx` 와 같다 — 현재
 * 페이지를 가운데 둔 슬라이딩 창 + 항상 첫/마지막 + 떨어져 있으면 말줄임.
 *
 * 색만 다르다: admin 은 현재 페이지를 주황으로 칠하지만, 검색 화면은 주황을 카테고리
 * 알약이 쓰고 있어 여기서는 **검정**을 쓴다 — 지도에서 선택된 핀이 검정으로 반전되는
 * 것과 같은 어휘다.
 *
 * 창 크기는 데스크톱 5칸 / 모바일 3칸. 모바일 드로어 폭에서는 5칸이 넘친다.
 */
export function SearchPagination({
  page,
  totalPages,
  onPageChange,
  previousLabel,
  nextLabel,
}: SearchPaginationProps) {
  if (totalPages <= 1) return null;

  const renderWindow = (size: number) => {
    const pages = buildPageWindow(page, totalPages, size);
    const first = pages[0];
    const last = pages[pages.length - 1];

    return (
      <>
        {first > 1 && (
          <>
            <PageButton
              n={1}
              active={page === 1}
              onClick={() => onPageChange(1)}
            />
            {first > 2 && <Ellipsis />}
          </>
        )}
        {pages.map((n) => (
          <PageButton
            key={n}
            n={n}
            active={n === page}
            onClick={() => onPageChange(n)}
          />
        ))}
        {last < totalPages && (
          <>
            {last < totalPages - 1 && <Ellipsis />}
            <PageButton
              n={totalPages}
              active={page === totalPages}
              onClick={() => onPageChange(totalPages)}
            />
          </>
        )}
      </>
    );
  };

  return (
    <nav
      className="flex items-center justify-center gap-1 py-8"
      aria-label={previousLabel}
    >
      <Arrow
        label={previousLabel}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft className="w-5 h-5" />
      </Arrow>

      {/* 모바일 3칸 / 데스크톱 5칸 — 같은 규칙, 다른 창 크기 */}
      <div className="flex items-center gap-1 sm:hidden">
        {renderWindow(3)}
      </div>
      <div className="hidden sm:flex items-center gap-1">
        {renderWindow(5)}
      </div>

      <Arrow
        label={nextLabel}
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight className="w-5 h-5" />
      </Arrow>
    </nav>
  );
}

function PageButton({
  n,
  active,
  onClick,
}: {
  n: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={cn(
        "h-9 w-9 rounded-full text-sm tabular-nums transition-colors",
        active
          ? "bg-neutral-900 text-white font-semibold"
          : "text-gray-600 hover:bg-gray-100",
      )}
    >
      {n}
    </button>
  );
}

function Ellipsis() {
  return (
    <span className="h-9 w-6 flex items-center justify-center text-gray-400 select-none">
      …
    </span>
  );
}

function Arrow({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={cn(
        "h-9 w-9 rounded-full flex items-center justify-center transition-colors",
        disabled
          ? "text-gray-300 pointer-events-none"
          : "text-gray-900 hover:bg-gray-100",
      )}
    >
      {children}
    </button>
  );
}
